import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, User, Sparkles, ArrowRight, ArrowLeft, Check, Plane, TrainFront } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { loadOffer, loadSearch, gbp } from "@/lib/constants";

const EXTRAS = [
  ["child_seat", "Child Seat"], ["meet_greet", "Meet & Greet"], ["flight_monitoring", "Flight Monitoring"],
  ["wheelchair", "Wheelchair Accessible"], ["extra_waiting", "Extra Waiting Time"],
];
const STEPS = ["Journey", "Your Details", "Requirements"];

export default function BookingFlow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const offer = loadOffer();
  const search = loadSearch();
  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState({
    first: user?.name?.split(" ")[0] || "", last: user?.name?.split(" ").slice(1).join(" ") || "",
    phone: user?.phone || "", email: user?.email || "", company: "",
  });
  const [extras, setExtras] = useState([]);
  const [notes, setNotes] = useState("");
  const [flight, setFlight] = useState("");
  const [terminal, setTerminal] = useState("");
  const [train, setTrain] = useState("");
  const [loading, setLoading] = useState(false);

  if (!offer || !search) {
    return <div className="mx-auto max-w-md px-4 py-20 text-center">
      <p className="text-slate-600">No taxi selected. Please start a search first.</p>
      <Button className="mt-4 bg-brand" onClick={() => navigate("/search")}>Search Taxis</Button>
    </div>;
  }

  const toggleExtra = (k) => setExtras((e) => e.includes(k) ? e.filter((x) => x !== k) : [...e, k]);

  const submit = async () => {
    if (!customer.first || !customer.phone || !customer.email) { toast.error("Please complete your contact details"); setStep(1); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        service_type: search.service_type, pickup: search.pickup, dropoff: search.dropoff,
        date: search.date, time: search.time,
        passengers: search.passengers_detail || { adults: search.passengers, children: 0, infants: 0 },
        luggage: search.luggage_detail || { small: 0, large: 0 },
        vehicle_id: offer.vehicle_id, vehicle_key: offer.vehicle_key,
        customer: { name: `${customer.first} ${customer.last}`.trim(), email: customer.email, phone: customer.phone, company: customer.company, user_id: user?.id || null },
        extras, notes, flight_number: flight || null, terminal: terminal || null, train_number: train || null,
        hours: search.hours, created_by: "customer",
      });
      navigate(`/checkout/${data.id}`);
    } catch { toast.error("Could not create booking"); }
    finally { setLoading(false); }
  };

  const isAirport = search.service_type === "airport";
  const isStation = search.service_type === "station";

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Stepper */}
      <div className="mb-8 flex items-center justify-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${i <= step ? "bg-brand text-white" : "bg-slate-200 text-slate-500"}`}>
              {i < step ? <Check size={16} /> : i + 1}
            </div>
            <span className={`hidden text-sm font-medium sm:inline ${i <= step ? "text-navy" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-slate-200 sm:w-10" />}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6">
          {step === 0 && (
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-navy"><MapPin size={18} className="text-brand" /> Journey Details</h2>
              <div className="mt-4 grid gap-3 text-sm">
                <Row label="Pickup" value={search.pickup.name} />
                {search.dropoff && <Row label="Destination" value={search.dropoff.name} />}
                <Row label="Date" value={search.date} />
                <Row label="Time" value={search.time} />
                <Row label="Passengers" value={search.passengers} />
                <Row label="Vehicle" value={offer.title} />
              </div>
              <Button className="mt-6 w-full gap-2 bg-brand hover:bg-brand/90" onClick={() => setStep(1)} data-testid="step-next-0">Continue <ArrowRight size={16} /></Button>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-navy"><User size={18} className="text-brand" /> Customer Details</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div><Label>First Name</Label><Input value={customer.first} onChange={(e) => setCustomer({ ...customer, first: e.target.value })} data-testid="cust-first" /></div>
                <div><Label>Last Name</Label><Input value={customer.last} onChange={(e) => setCustomer({ ...customer, last: e.target.value })} data-testid="cust-last" /></div>
                <div><Label>Mobile Number</Label><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="cust-phone" /></div>
                <div><Label>Email</Label><Input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} data-testid="cust-email" /></div>
                <div className="sm:col-span-2"><Label>Company Name (optional)</Label><Input value={customer.company} onChange={(e) => setCustomer({ ...customer, company: e.target.value })} data-testid="cust-company" /></div>
              </div>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={() => setStep(0)} className="gap-1"><ArrowLeft size={16} /> Back</Button>
                <Button className="flex-1 gap-2 bg-brand hover:bg-brand/90" onClick={() => setStep(2)} data-testid="step-next-1">Continue <ArrowRight size={16} /></Button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-navy"><Sparkles size={18} className="text-brand" /> Additional Requirements</h2>
              {isAirport && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 rounded-xl bg-brand-light p-4">
                  <div className="sm:col-span-2 flex items-center gap-2 text-sm font-medium text-navy"><Plane size={15} className="text-brand" /> Flight details</div>
                  <div><Label>Flight Number</Label><Input value={flight} onChange={(e) => setFlight(e.target.value)} data-testid="flight-number" placeholder="BA286" /></div>
                  <div><Label>Terminal</Label><Input value={terminal} onChange={(e) => setTerminal(e.target.value)} data-testid="terminal" placeholder="Terminal 5" /></div>
                </div>
              )}
              {isStation && (
                <div className="mt-4 rounded-xl bg-brand-light p-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-navy"><TrainFront size={15} className="text-brand" /> Train details</div>
                  <Label>Train Number</Label><Input value={train} onChange={(e) => setTrain(e.target.value)} data-testid="train-number" placeholder="e.g. 1A23" />
                </div>
              )}
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {EXTRAS.map(([k, l]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                    <Checkbox checked={extras.includes(k)} onCheckedChange={() => toggleExtra(k)} data-testid={`extra-${k}`} />
                    <span className="text-sm text-slate-700">{l}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4"><Label>Additional instructions for driver</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes" placeholder="Any special instructions" /></div>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-1"><ArrowLeft size={16} /> Back</Button>
                <Button className="flex-1 bg-brand hover:bg-brand/90" onClick={submit} disabled={loading} data-testid="proceed-checkout">{loading ? "Creating…" : "Continue to Checkout"}</Button>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div>
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
            <img src={offer.image} alt="" className="h-32 w-full rounded-xl object-cover" />
            <h3 className="mt-3 font-heading font-semibold text-navy">{offer.title}</h3>
            <p className="text-sm text-slate-500">{offer.operator_name}</p>
            <div className="my-4 h-px bg-slate-200" />
            <div className="flex items-center justify-between"><span className="text-sm text-slate-500">Estimated fare</span><span className="font-heading text-2xl font-bold text-navy">{gbp(offer.total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">{label}</span><span className="font-medium text-navy">{value}</span></div>;
}
